import {
  Button,
  Card,
  Form as AntForm,
  Input,
  Modal,
  PageHeader,
  Radio,
  Select,
  Space,
  Typography,
  message,
} from 'antd'
import { Form, Formik } from 'formik'
import get from 'lodash.get'
import set from 'lodash.set'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import { createClient, getClients } from '../network/Clients'
import { createOrder as createOrderRequest } from '../network/Orders'
import { useAuth } from '../hooks/useAuth'
import Step1 from './Orders/steps/Step1'
import Step5 from './Orders/steps/Step5'

const { Text } = Typography

// ─── Quick-create customer modal ─────────────────────────────────────────────

const CreateCustomerModal = ({ storeId, onCreated, onClose }) => {
  const [form] = AntForm.useForm()
  const queryClient = useQueryClient()

  const createCustomer = useMutation(createClient, {
    onSuccess: (data) => {
      const created = data?.user ?? data
      message.success(
        `Customer ${created.firstName} ${created.lastName} created`
      )
      queryClient.invalidateQueries('getClientsFastSale')
      onCreated(created.id ?? created._id)
    },
    onError: () => message.error('Failed to create customer'),
  })

  const handleOk = () => {
    form.validateFields().then((values) => {
      createCustomer.mutate({
        ...values,
        store: storeId,
        complexion: {},
        bodyMeasurements: { height: 170, weight: 89 },
      })
    })
  }

  return (
    <Modal
      title="Create new customer"
      open
      onOk={handleOk}
      onCancel={onClose}
      okText="Create"
      confirmLoading={createCustomer.isLoading}
      destroyOnClose
    >
      <AntForm form={form} layout="vertical" initialValues={{ gender: 'male' }}>
        <AntForm.Item
          label="Gender"
          name="gender"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Radio.Group buttonStyle="solid" optionType="button">
            <Radio.Button value="male">Man</Radio.Button>
            <Radio.Button value="female">Woman</Radio.Button>
          </Radio.Group>
        </AntForm.Item>
        <AntForm.Item
          label="First name"
          name="firstName"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Input placeholder="First name" />
        </AntForm.Item>
        <AntForm.Item
          label="Last name"
          name="lastName"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Input placeholder="Last name" />
        </AntForm.Item>
        <AntForm.Item label="Phone number" name="phone">
          <Input placeholder="Phone number (optional)" />
        </AntForm.Item>
      </AntForm>
    </Modal>
  )
}

// ─── Optional customer step ───────────────────────────────────────────────────

const OptionalCustomerStep = ({ storeId, values, handleChange }) => {
  const [showCreate, setShowCreate] = useState(false)

  const clients = useQuery(
    [
      'getClientsFastSale',
      {
        sort: ['firstName', 'ASC'],
        range: [0, 1000],
        filter: {},
        tabFilter: {},
      },
    ],
    getClients,
    { refetchOnWindowFocus: false }
  )

  const list = get(clients.data, 'list', [])

  const handleCreated = (newId) => {
    handleChange({ target: { name: 'customer', value: newId } })
    setShowCreate(false)
  }

  return (
    <Card title="Optional Customer">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Text type="secondary">
          You can leave this empty and create the order without a customer.
        </Text>

        <Select
          allowClear
          showSearch
          optionFilterProp="children"
          placeholder="Retrieve an existing customer (optional)"
          value={values.customer || undefined}
          style={{ width: '60%' }}
          onChange={(customerId) =>
            handleChange({
              target: { name: 'customer', value: customerId || undefined },
            })
          }
          loading={clients.isLoading}
        >
          {list.map((client) => (
            <Select.Option key={client.id} value={client.id}>
              {`${client.firstName || ''} ${client.lastName || ''}`.trim()}
              {(client.email || client.phone)
                ? ` — ${client.email || client.phone}`
                : ''}
            </Select.Option>
          ))}
        </Select>

        <Button type="link" style={{ padding: 0 }} onClick={() => setShowCreate(true)}>
          + Create a new customer
        </Button>

        {showCreate && (
          <CreateCustomerModal
            storeId={storeId}
            onCreated={handleCreated}
            onClose={() => setShowCreate(false)}
          />
        )}
      </Space>
    </Card>
  )
}

// ─── Main Fast Sale page ──────────────────────────────────────────────────────

const FastSale = ({ history }) => {
  const { user } = useAuth()
  const { storeId } = useParams()
  const [step, setStep] = useState(1)

  const createOrder = useMutation(createOrderRequest, {
    onSuccess: () => history.push('/orders'),
    onError: (error) =>
      message.error(
        error.response?.data?.debugLog?.message ||
          error.response?.data?.message ||
          'An error occurred'
      ),
  })

  return (
    <div>
      <PageHeader
        className="site-page-header"
        title="Fast Sale"
        onBack={() => history.push('/')}
        style={{ backgroundColor: '#fff' }}
      />
      <Formik
        initialValues={{
          store: storeId,
          channel: 'store',
          currency: '',
          customer: undefined,
          orderProducts: [],
          discount: 0,
          comment: '',
          validated: false,
        }}
        validate={(values) => {
          const errors = {}
          if (step === 1) {
            if (!values.orderProducts.length) {
              errors.orderProducts = 'At least a product is required'
            } else {
              for (let i = 0; i < values.orderProducts.length; i++) {
                const currentProduct = values.orderProducts[i]
                if (currentProduct.orderProductType === 'fixed_design') {
                  if (!get(currentProduct, 'orderFixedDesign.price', null)) {
                    set(
                      errors,
                      `orderProducts.${i}.orderFixedDesign.price`,
                      'Required'
                    )
                  }
                } else if (currentProduct.orderProductType === 'collection') {
                  const col = get(values, `orderProducts.${i}.orderCollection`, {})
                  if (!col.product) {
                    set(errors, `orderProducts.${i}.orderCollection.product`, 'Select product')
                  }
                  const sizes = get(col, 'sizes', [])
                  if (sizes.length > 0 && !col.size) {
                    set(errors, `orderProducts.${i}.orderCollection.size`, 'Select size')
                  }
                  if (!col.price && col.price !== 0) {
                    set(errors, `orderProducts.${i}.orderCollection.price`, 'The price is required')
                  }
                } else if (currentProduct.orderProductType !== undefined) {
                  set(
                    errors,
                    `orderProducts.${i}.orderProductType`,
                    'Only fixed design and fixed SKU are allowed in fast sale'
                  )
                }
              }
            }
          }
          return errors
        }}
        onSubmit={(values) => {
          let paymentStatus = 'unpaid'
          if (values.balance === 0) paymentStatus = 'paid'
          else if (values.deposit || values.deposit2) paymentStatus = 'deposit'

          createOrder.mutate({
            ...values,
            orderType: 'fast_sale',
            paymentStatus,
            ...(user?.id != null ? { createdBy: user.id } : {}),
          })
        }}
      >
        {({ values, errors, handleChange, validateForm }) => (
          <Form style={{ padding: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {step === 1 && (
                <Step1
                  values={values}
                  errors={errors}
                  validateForm={validateForm}
                  handleChange={handleChange}
                  handleBack={() => history.push('/')}
                  handleNext={() => setStep(2)}
                  isFastSale
                />
              )}

              {step === 2 && (
                <>
                  <OptionalCustomerStep
                    storeId={storeId}
                    values={values}
                    handleChange={handleChange}
                  />
                  <Space style={{ marginTop: 16 }}>
                    <Button onClick={() => setStep(1)} type="secondary">
                      Previous
                    </Button>
                    <Button onClick={() => setStep(3)} type="primary">
                      Next
                    </Button>
                  </Space>
                </>
              )}

              {step === 3 && (
                <>
                  <Step5
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                  />
                  <Space style={{ marginTop: 16 }}>
                    <Button onClick={() => setStep(2)} type="secondary">
                      Previous
                    </Button>
                    <Button
                      loading={createOrder.isLoading}
                      disabled={Object.keys(errors).length > 0}
                      type="primary"
                      htmlType="submit"
                    >
                      Finish order
                    </Button>
                  </Space>
                </>
              )}
            </Space>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default FastSale
